/**
 * API client: bearer access token in memory, silent refresh via httpOnly
 * cookie (same-origin through the Vite/nginx proxy), envelope unwrapping.
 */
export interface ApiMeta {
  total: number;
  page: number;
  limit: number;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the automatic 401→refresh→retry loop (used by auth calls). */
  skipRefresh?: boolean;
}

interface Envelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  details?: unknown;
  meta?: ApiMeta;
}

async function rawRequest<T>(path: string, options: RequestOptions): Promise<Envelope<T>> {
  const response = await fetch(`/v1${path}`, {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
    credentials: 'same-origin',
  });
  let envelope: Envelope<T>;
  try {
    envelope = (await response.json()) as Envelope<T>;
  } catch {
    envelope = { success: false, error: `Request failed (${response.status})` };
  }
  if (!response.ok || !envelope.success) {
    throw new ApiError(
      envelope.error ?? 'Something went wrong',
      response.status,
      envelope.code,
      envelope.details,
    );
  }
  return envelope;
}

/** Refresh once even when many requests 401 simultaneously. */
export async function tryRefresh(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const envelope = await rawRequest<{ accessToken: string }>('/auth/refresh', {
        method: 'POST',
        skipRefresh: true,
      });
      accessToken = envelope.data?.accessToken ?? null;
      return Boolean(accessToken);
    } catch {
      accessToken = null;
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    const envelope = await rawRequest<T>(path, options);
    return envelope.data as T;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401 && !options.skipRefresh) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        const retry = await rawRequest<T>(path, { ...options, skipRefresh: true });
        return retry.data as T;
      }
      window.dispatchEvent(new CustomEvent('visora:unauthorized'));
    }
    throw error;
  }
}

/** Same as api() but returns { data, meta } for paginated lists. */
export async function apiPaged<T>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; meta?: ApiMeta }> {
  try {
    const envelope = await rawRequest<T>(path, options);
    return { data: envelope.data as T, meta: envelope.meta };
  } catch (error) {
    if (error instanceof ApiError && error.status === 401 && !options.skipRefresh) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        const retry = await rawRequest<T>(path, { ...options, skipRefresh: true });
        return { data: retry.data as T, meta: retry.meta };
      }
      window.dispatchEvent(new CustomEvent('visora:unauthorized'));
    }
    throw error;
  }
}

/** Direct-to-storage upload against a presigned PUT URL. */
export async function uploadToPresignedUrl(url: string, file: File): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!response.ok) throw new ApiError('Upload failed', response.status);
}
